/**
 * The market pool (website roadmap S31 gear market + S32 bazaar deals, terms version MARKET_POOL_TERMS = 15): sellers'
 * listings — seller, price, quantity, a weapon's or armor's stats, bonuses and rarity — that Omni-Torn users SAW on Torn's
 * Item Market, bazaar and auction house pages they viewed (sent only while the page was visible and focused: the
 * extension's page gate), kept on the Omni-Torn server a short while and shown to Omni-Torn users; plus Torn's public
 * auction-house sales (v2 `/market/auctionhouse`, read with lent keys or the server's own key — no sellers or buyers kept)
 * for an RW item's value. Pure: readers, checks, ranking, values and deals. Who sent a sighting is never kept or shown;
 * a seller's name is what Torn shows publicly on that page (anonymous Item Market sellers stay anonymous).
 */

import { RARITIES, type Rarity } from './loadout-intel';

type Obj = Record<string, unknown>;
const obj = (x: unknown): Obj | null => (x && typeof x === 'object' && !Array.isArray(x) ? (x as Obj) : null);
const num = (x: unknown): number | null => (typeof x === 'number' && Number.isFinite(x) ? x : typeof x === 'string' && /^\s*-?[\d,]+(\.\d+)?\s*$/.test(x) ? Number(x.replace(/[,\s]/g, '')) : null);
const posInt = (x: unknown, max = 1e13): number | null => {
  const n = num(x);
  return n !== null && Number.isInteger(n) && n > 0 && n <= max ? n : null;
};

/** The terms version that names the market pool: senders below it are refused; the extension sends nothing below it. */
export const MARKET_POOL_TERMS = 15;
/** Item Market and bazaar listings are kept this long after they were last seen (then dropped). */
export const POOL_KEEP_MS = 24 * 3_600_000;
/** Deals, $1 bazaars and the bazaar viewer use only listings seen this recently. */
export const POOL_FRESH_MS = 2 * 3_600_000;
/**
 * A running auction is kept until an hour after it ends (POOL_AUCTION_AFTER_MS); its end time must be read and at most
 * POOL_AUCTION_MAX_MS ahead — an auction row without one isn't kept.
 */
export const POOL_AUCTION_AFTER_MS = 3_600_000;
export const POOL_AUCTION_MAX_MS = 8 * 86_400_000;
/** Torn's finished auctions (public API data; no sellers or buyers kept) are kept this many days for RW values. */
export const AUCTION_SALES_KEEP_DAYS = 90;
/** Listings in one sighting. */
export const POOL_MAX_BATCH = 200;
/** Sightings one sender may send a minute (the server's limit; the extension stays under it). */
export const POOL_POSTS_PER_MINUTE = 30;
/** The pool's size cap (the oldest go first). */
export const POOL_MAX_ROWS = 300_000;
/** The auction-house reader: a round every 30 minutes, at most 3 calls a round, 100 sales a call. */
export const AUCTION_ROUND_MS = 30 * 60_000;
export const AUCTION_ROUND_CALLS = 3;
export const AUCTION_PAGE = 100;
/** Rows a gear or deals answer carries at most. */
export const POOL_ANSWER_MAX = 300;
/** The lists-sync list of the player's favorite items with a target price (core/list-sync.ts SYNCED_LISTS). */
export const MARKET_FAVORITES_LIST = 'omnitorn.market.favorites';
export const MARKET_FAVORITES_MAX = 100;

export type PoolSource = 'itemmarket' | 'bazaar' | 'auction';
export const POOL_SOURCES: readonly PoolSource[] = ['itemmarket', 'bazaar', 'auction'];
/** Torn's item types that are gear (weapons and armor). */
export const GEAR_TYPES = ['Primary', 'Secondary', 'Melee', 'Defensive'] as const;
export const isGearType = (t: string | null | undefined): boolean => !!t && (GEAR_TYPES as readonly string[]).includes(t);

export interface GearStats {
  damage: number | null;
  accuracy: number | null;
  armor: number | null;
  quality: number | null;
}

export interface GearBonus {
  /** Torn's bonus name ("Expose", "Revitalize"). */
  title: string;
  /** Its value (percent, turns…) as Torn shows it; null when the page didn't say. */
  value: number | null;
}

/** One listing as a page showed it (what the extension sends). */
export interface PoolListingIn {
  source: PoolSource;
  itemId: number;
  /** The seller as Torn shows them publicly on that page; null: anonymous or not shown. */
  sellerId: number | null;
  sellerName: string | null;
  /** Price each (an auction: the current bid). */
  price: number;
  qty: number;
  /** Torn's id of this one copy (armory id / uid), for gear. */
  uid: number | null;
  stats: GearStats | null;
  bonuses: GearBonus[];
  rarity: Rarity | null;
  /** An auction's listing id and when it ends (ms). */
  auctionId: number | null;
  endsAt: number | null;
}

/** A kept listing: when it was last seen (ms). */
export interface PoolListing extends PoolListingIn {
  seenAt: number;
}

/**
 * What one page sight replaces: the whole list of one seller's bazaar (when the page loaded it all), or one item's
 * Item Market list (the cheapest the page showed). Older listings in that scope that weren't seen again go.
 */
export type PoolScope = { source: 'bazaar'; sellerId: number } | { source: 'itemmarket'; itemId: number };

export interface PoolSighting {
  listings: PoolListingIn[];
  scope: PoolScope | null;
}

export interface PoolOfferAnswer {
  ok: boolean;
  kept: number;
  skipped: number;
  message?: string;
}

/** A Torn player name (letters, digits, _ and -), else null. */
export function cleanSellerName(x: unknown): string | null {
  return typeof x === 'string' && /^[\w-]{1,30}$/.test(x.trim()) ? x.trim() : null;
}

/** "Revitalize" from "<b>Revitalize</b><br/>10% chance…", "bonus-attachment-home-run", "home_run"… (null: not a name). */
export function bonusTitle(x: unknown): string | null {
  if (typeof x !== 'string') return null;
  let s = x;
  const bold = /<b>([^<]{1,40})<\/b>/i.exec(s);
  if (bold) s = bold[1]!;
  else if (/[<>]/.test(s)) return null;
  s = s.replace(/^.*bonus-attachment-/i, '').replace(/[_-]+/g, ' ').trim();
  if (/^(blank|item )/i.test(s) || /bonus \d+$/i.test(s)) return null;
  const words = s.split(/\s+/).filter(Boolean);
  if (!words.length || words.length > 4) return null;
  const t = words.map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ');
  return /^[A-Za-z][A-Za-z ']{2,29}$/.test(t) ? t : null;
}

/** A bonus's value: a number, or the first number in "10% chance…" / "Revitalize 10%". */
export function bonusValue(x: unknown): number | null {
  const n = num(x);
  if (n !== null) return n >= 0 && n <= 10_000 ? n : null;
  if (typeof x !== 'string') return null;
  const m = /(\d+(?:\.\d+)?)\s*(%|turns?|x\b)?/i.exec(x.replace(/<[^>]*>/g, ' '));
  const v = m ? Number(m[1]) : NaN;
  return Number.isFinite(v) && v >= 0 && v <= 10_000 ? v : null;
}

/** "yellow" from "glow-yellow", "item-plate glow-orange", "red"… */
export function rarityOf(x: unknown): Rarity | null {
  if (typeof x !== 'string') return null;
  const m = /(yellow|orange|red)/i.exec(x);
  return m ? (m[1]!.toLowerCase() as Rarity) : null;
}

export const rarityRank = (r: Rarity | null): number => (r ? RARITIES.indexOf(r) + 1 : 0);

function cleanStats(x: unknown): GearStats | null {
  const o = obj(x);
  if (!o) return null;
  const s = (v: unknown, max: number) => {
    const n = num(v);
    return n !== null && n >= 0 && n <= max ? Math.round(n * 100) / 100 : null;
  };
  const r: GearStats = { damage: s(o.damage ?? o.dmg, 1000), accuracy: s(o.accuracy ?? o.acc, 1000), armor: s(o.armor ?? o.armour ?? o.arm, 1000), quality: s(o.quality, 1000) };
  return r.damage === null && r.accuracy === null && r.armor === null && r.quality === null ? null : r;
}

function cleanBonuses(x: unknown): GearBonus[] {
  if (!Array.isArray(x)) return [];
  const out: GearBonus[] = [];
  for (const b of x.slice(0, 6)) {
    const o = obj(b);
    const title = bonusTitle(o ? (o.title ?? o.name ?? o.bonus ?? o.class) : b);
    if (!title || out.some((y) => y.title === title)) continue;
    out.push({ title, value: o ? (bonusValue(o.value ?? o.val ?? o.percent) ?? bonusValue(o.description ?? o.desc ?? o.title)) : bonusValue(b) });
    if (out.length >= 4) break;
  }
  return out;
}

/**
 * One listing as the extension sent it, checked (it crosses from a page); null when unusable. Every sighting names its
 * seller (a profile Torn links): anonymous Item Market rows and auction rows without a seller link are dropped — they could
 * be the viewer's own, and nothing about the viewer is kept. An auction needs its end time.
 */
export function cleanPoolListing(x: unknown, now: number): PoolListingIn | null {
  const o = obj(x);
  if (!o) return null;
  const source = typeof o.source === 'string' && (POOL_SOURCES as readonly string[]).includes(o.source) ? (o.source as PoolSource) : null;
  const itemId = posInt(o.itemId, 1e6);
  const price = posInt(o.price, 1e12);
  const qty = posInt(o.qty ?? 1, 1e7);
  if (!source || !itemId || !price || !qty) return null;
  const sellerId = posInt(o.sellerId, 1e8);
  const auctionId = source === 'auction' ? posInt(o.auctionId, 1e12) : null;
  if (source === 'auction' && !auctionId) return null;
  // A sighting always names its seller (one Torn shows): an anonymous or unlinked row could be the viewer's own — never kept.
  if (!sellerId) return null;
  const ends = num(o.endsAt);
  const endsAt = ends !== null && ends > now - POOL_AUCTION_AFTER_MS && ends < now + POOL_AUCTION_MAX_MS ? Math.round(ends) : null;
  // A running auction is kept only until an hour after it ends: one without a readable end time isn't kept at all.
  if (source === 'auction' && endsAt === null) return null;
  return {
    source,
    itemId,
    sellerId,
    sellerName: sellerId ? cleanSellerName(o.sellerName) : null,
    price,
    qty: source === 'auction' ? 1 : qty,
    uid: posInt(o.uid),
    stats: cleanStats(o.stats),
    bonuses: cleanBonuses(o.bonuses),
    rarity: rarityOf(o.rarity),
    auctionId,
    endsAt: source === 'auction' ? endsAt : null,
  };
}

/** A sighting as the extension sent it (`POST /api/market/pool`), checked: at most POOL_MAX_BATCH usable listings. */
export function cleanSighting(x: unknown, now: number): PoolSighting | null {
  const o = obj(x);
  if (!o || !Array.isArray(o.listings)) return null;
  const listings = (o.listings as unknown[])
    .slice(0, POOL_MAX_BATCH)
    .map((l) => cleanPoolListing(l, now))
    .filter((l): l is PoolListingIn => !!l);
  const sc = obj(o.scope);
  let scope: PoolScope | null = null;
  if (sc?.source === 'bazaar' && posInt(sc.sellerId, 1e8)) scope = { source: 'bazaar', sellerId: posInt(sc.sellerId, 1e8)! };
  if (sc?.source === 'itemmarket' && posInt(sc.itemId, 1e6)) scope = { source: 'itemmarket', itemId: posInt(sc.itemId, 1e6)! };
  // A scope only covers listings it names: one seller's bazaar, one item's Item Market list.
  const sc2 = scope;
  if (sc2?.source === 'bazaar' && listings.some((l) => l.source !== 'bazaar' || l.sellerId !== sc2.sellerId)) scope = null;
  if (sc2?.source === 'itemmarket' && listings.some((l) => l.source !== 'itemmarket' || l.itemId !== sc2.itemId)) scope = null;
  return { listings, scope };
}

/** The pool's key of a listing: one per bazaar seller, item and copy; per Item Market seller, item, copy and price; per auction. */
export function poolKey(l: PoolListingIn): string {
  if (l.source === 'auction') return `a:${l.auctionId}`;
  if (l.source === 'bazaar') return `b:${l.sellerId}:${l.itemId}:${l.uid ?? 0}`;
  return `m:${l.itemId}:${l.sellerId ?? 'anon'}:${l.uid ?? 0}:${l.price}`;
}

// ---------------------------------------------------------------- what pages show (the extension reads with these)

/**
 * Torn's bazaar page answer (bazaar.php?sid=bazaarData&step=getBazaarItems; shape as TornTools reads it):
 * `{ list: [{ itemID, amount, price, armoryID, dmg, acc, arm, bonuses: [{ class, title?, value? }], glowClass? }], total,
 * start? }`. The seller is the bazaar's owner (the page's `userId`). `complete`: the answer holds the whole bazaar.
 */
export function readBazaarItemsAnswer(body: unknown, sellerId: number, sellerName: string | null, now: number): { listings: PoolListingIn[]; complete: boolean } {
  const b = obj(body);
  const list = Array.isArray(b?.list) ? (b!.list as unknown[]) : [];
  const listings = list
    .map((x) => {
      const o = obj(x);
      if (!o) return null;
      return cleanPoolListing(
        {
          source: 'bazaar',
          itemId: o.itemID ?? o.itemId ?? o.ID,
          sellerId,
          sellerName,
          price: o.price,
          qty: o.amount ?? o.quantity ?? 1,
          uid: o.armoryID ?? o.armoryId ?? o.uid,
          stats: { damage: o.dmg ?? o.damage, accuracy: o.acc ?? o.accuracy, armor: o.arm ?? o.armor, quality: o.quality },
          bonuses: o.bonuses,
          rarity: o.glowClass ?? o.glow ?? o.rarity,
        },
        now,
      );
    })
    .filter((l): l is PoolListingIn => !!l)
    .map((l) => (isPlainStats(l) ? { ...l, stats: null } : l));
  const total = num(b?.total);
  const start = num(b?.start) ?? 0;
  return { listings, complete: start === 0 && total !== null && list.length === total };
}

/** Items that aren't gear carry Torn's stats as zeros: those are no stats. */
const isPlainStats = (l: PoolListingIn): boolean => !l.uid && !l.bonuses.length && !l.rarity && (!l.stats || (!l.stats.damage && !l.stats.accuracy && !l.stats.armor));

/** "$1,234,567" / "1.2m" / "$40,901" → a whole number of dollars (null: no money in it). */
export function moneyText(x: string | null | undefined): number | null {
  if (!x) return null;
  const m = /\$?\s*([\d,]+(?:\.\d+)?)\s*([kmb])?/i.exec(x);
  if (!m) return null;
  const n = Number(m[1]!.replace(/,/g, '')) * ({ k: 1e3, m: 1e6, b: 1e9 }[(m[2] ?? '').toLowerCase() as 'k' | 'm' | 'b'] ?? 1);
  return Number.isFinite(n) && n > 0 ? Math.round(n) : null;
}

// ---------------------------------------------------------------- Torn's finished auctions (API)

/** One finished auction (Torn v2 `/market/auctionhouse`), without its seller and buyer (never kept). */
export interface AuctionSale {
  /** Torn's auction listing id. */
  id: number;
  itemId: number;
  itemName: string | null;
  itemType: string | null;
  uid: number | null;
  price: number;
  bids: number;
  /** When it ended (ms). */
  endedAt: number;
  rarity: Rarity | null;
  bonuses: GearBonus[];
  stats: GearStats | null;
}

/**
 * Torn v2 `/market/auctionhouse` (public): `{ auctionhouse: [{ id, seller, buyer, timestamp, price, bids, item: { id, uid,
 * name, type, sub_type, stats, bonuses: [{ id, title, description, value }], rarity } }] }`. Seller and buyer are read past.
 */
export function readAuctionSales(body: unknown): AuctionSale[] | null {
  const b = obj(body);
  if (!b || !Array.isArray(b.auctionhouse)) return null;
  const out: AuctionSale[] = [];
  for (const x of b.auctionhouse as unknown[]) {
    const o = obj(x);
    const item = obj(o?.item);
    const id = posInt(o?.id, 1e12);
    const itemId = posInt(item?.id, 1e6);
    const price = posInt(o?.price, 1e13);
    const ts = posInt(o?.timestamp, 1e11);
    if (!id || !itemId || !price || !ts) continue;
    out.push({
      id,
      itemId,
      itemName: typeof item!.name === 'string' ? item!.name.slice(0, 60) : null,
      itemType: typeof item!.type === 'string' ? item!.type.slice(0, 30) : null,
      uid: posInt(item!.uid),
      price,
      bids: Math.max(0, Math.round(num(o!.bids) ?? 0)),
      endedAt: ts * 1000,
      rarity: rarityOf(item!.rarity),
      bonuses: cleanBonuses(item!.bonuses),
      stats: cleanStats(item!.stats),
    });
  }
  return out;
}

// ---------------------------------------------------------------- RW value from auction sales

export interface RwValue {
  /** The middle price of the comparable sales. */
  value: number;
  /** The quarter and three-quarter prices. */
  low: number;
  high: number;
  comps: number;
  basis: 'item, rarity and bonus' | 'item and rarity' | 'rarity and bonus';
}

function quantile(sorted: readonly number[], q: number): number {
  const i = (sorted.length - 1) * q;
  const lo = Math.floor(i);
  const hi = Math.ceil(i);
  return Math.round(sorted[lo]! + (sorted[hi]! - sorted[lo]!) * (i - lo));
}

/**
 * What an RW item is worth from Torn's finished auctions: the median of comparable sales — the same item, rarity and first
 * bonus (with a bonus value within a fifth when the item has one and ≥ 2 such sales), else the same item and rarity, else
 * the same rarity and bonus on any item. At least 2 comparable sales (3 for the last). Null: not enough to go on.
 */
export function rwValue(g: { itemId: number; rarity: Rarity | null; bonuses: readonly GearBonus[] }, sales: readonly AuctionSale[]): RwValue | null {
  if (!g.rarity && !g.bonuses.length) return null;
  const bonus = g.bonuses[0] ?? null;
  const sameBonus = (s: AuctionSale) => !!bonus && s.bonuses.some((b) => b.title === bonus.title);
  const pick = (list: AuctionSale[], basis: RwValue['basis'], min: number): RwValue | null => {
    if (list.length < min) return null;
    const p = list.map((s) => s.price).sort((a, b) => a - b);
    return { value: quantile(p, 0.5), low: quantile(p, 0.25), high: quantile(p, 0.75), comps: p.length, basis };
  };
  const item = sales.filter((s) => s.itemId === g.itemId && s.rarity === g.rarity);
  if (bonus) {
    const withBonus = item.filter(sameBonus);
    const close = bonus.value === null ? [] : withBonus.filter((s) => {
      const v = s.bonuses.find((b) => b.title === bonus.title)?.value;
      return v !== null && v !== undefined && Math.abs(v - bonus.value!) <= Math.max(1, bonus.value! * 0.2);
    });
    const r = pick(close.length >= 2 ? close : withBonus, 'item, rarity and bonus', 2);
    if (r) return r;
  }
  return pick(item, 'item and rarity', 2) ?? (bonus ? pick(sales.filter((s) => s.rarity === g.rarity && sameBonus(s)), 'rarity and bonus', 3) : null);
}

// ---------------------------------------------------------------- gear

export type GearSort = 'bonus' | 'price' | 'damage' | 'accuracy' | 'armor' | 'quality' | 'rarity' | 'vsValue';
export const GEAR_SORTS: readonly GearSort[] = ['bonus', 'price', 'damage', 'accuracy', 'armor', 'quality', 'rarity', 'vsValue'];

export interface GearFilter {
  itemId?: number | null;
  /** Torn's item type (Primary, Secondary, Melee, Defensive). */
  type?: string | null;
  /** At least this rarity. */
  rarity?: Rarity | null;
  /** A bonus whose name holds this (case ignored). */
  bonus?: string | null;
  /** That bonus (or any, without a name) at least this much. */
  minBonus?: number | null;
  maxPrice?: number | null;
  source?: PoolSource | null;
  sort?: GearSort;
}

/** A gear listing on the Gear page: the listing, its item's name and type, its RW value and the price against it. */
export interface GearRow extends PoolListing {
  itemName: string | null;
  itemType: string | null;
  rw: RwValue | null;
  /** Price against the RW value (percent; −20 = a fifth under). */
  vsValue: number | null;
}

export interface GearAnswer {
  rows: GearRow[];
  /** Matching before the cap. */
  matched: number;
  /** One item asked: its newest finished auctions (no sellers or buyers). */
  sales: AuctionSale[];
  /** Finished auctions kept, and the newest one's end (ms). */
  salesKept: number;
  salesNewest: number | null;
  /** Listings Omni-Torn users saw are kept from senders on this terms version. */
  termsNeeded: number;
  at: number;
}

/** A listing is gear when it carries what makes one copy differ (a copy id, stats, bonuses or a rarity). */
export const isGearListing = (l: PoolListingIn): boolean => !!(l.uid || l.stats || l.bonuses.length || l.rarity);

/** The best bonus value (the named one when a bonus is asked). */
export function bonusScore(l: Pick<PoolListingIn, 'bonuses'>, name?: string | null): number {
  const q = name?.trim().toLowerCase();
  const list = q ? l.bonuses.filter((b) => b.title.toLowerCase().includes(q)) : l.bonuses;
  return list.reduce((m, b) => Math.max(m, b.value ?? 0), 0);
}

export function gearMatches(l: GearRow, f: GearFilter): boolean {
  if (f.itemId && l.itemId !== f.itemId) return false;
  if (f.type && l.itemType !== f.type) return false;
  if (f.rarity && rarityRank(l.rarity) < rarityRank(f.rarity)) return false;
  if (f.source && l.source !== f.source) return false;
  if (f.maxPrice && l.price > f.maxPrice) return false;
  const q = f.bonus?.trim().toLowerCase();
  if (q && !l.bonuses.some((b) => b.title.toLowerCase().includes(q))) return false;
  if (f.minBonus && bonusScore(l, q) < f.minBonus) return false;
  return true;
}

/** Best first: biggest bonus / stats / rarity, cheapest, or furthest under its RW value. */
export function sortGear(rows: GearRow[], sort: GearSort = 'bonus', bonus?: string | null): GearRow[] {
  const by: Record<GearSort, (r: GearRow) => number> = {
    bonus: (r) => -bonusScore(r, bonus),
    price: (r) => r.price,
    damage: (r) => -(r.stats?.damage ?? -1),
    accuracy: (r) => -(r.stats?.accuracy ?? -1),
    armor: (r) => -(r.stats?.armor ?? -1),
    quality: (r) => -(r.stats?.quality ?? -1),
    rarity: (r) => -rarityRank(r.rarity),
    vsValue: (r) => r.vsValue ?? 1e9,
  };
  const f = by[sort] ?? by.bonus;
  return rows.sort((a, b) => f(a) - f(b) || rarityRank(b.rarity) - rarityRank(a.rarity) || a.price - b.price || b.seenAt - a.seenAt);
}

export const vsPct = (price: number, value: number | null | undefined): number | null => (value && value > 0 ? Math.round(((price - value) / value) * 1000) / 10 : null);

/** Parse the Gear page's query (`GET /api/market/gear?…`). */
export function gearFilterOf(q: URLSearchParams): GearFilter {
  const n = (k: string, max: number) => {
    const v = Number(q.get(k));
    return Number.isFinite(v) && v > 0 && v <= max ? v : null;
  };
  const type = q.get('type');
  const rarity = rarityOf(q.get('rarity'));
  const source = q.get('source');
  const sort = q.get('sort');
  const bonus = (q.get('bonus') ?? '').trim().slice(0, 30);
  return {
    itemId: n('item', 1e6),
    type: type && isGearType(type) ? type : null,
    rarity,
    bonus: /^[A-Za-z ']{1,30}$/.test(bonus) ? bonus : null,
    minBonus: n('minBonus', 10_000),
    maxPrice: n('maxPrice', 1e13),
    source: source && (POOL_SOURCES as readonly string[]).includes(source) ? (source as PoolSource) : null,
    sort: sort && (GEAR_SORTS as readonly string[]).includes(sort) ? (sort as GearSort) : 'bonus',
  };
}

// ---------------------------------------------------------------- deals

export interface DealRow extends PoolListing {
  itemName: string | null;
  /** Torn's market value of the item. */
  value: number | null;
  /** Percent under that value (positive: cheaper). */
  below: number | null;
  /** Reselling the lot at market value. */
  profit: number | null;
}

export interface DealsAnswer {
  /** Bazaar and Item Market listings under market value, most profit first. */
  deals: DealRow[];
  /** Bazaars selling at $1. */
  dollar: DealRow[];
  /** Your favorites' cheapest fresh listing each (when asked with ?fav=…). */
  favorites: DealRow[];
  /** Listings used are at most this old (ms). */
  freshMs: number;
  termsNeeded: number;
  at: number;
}

/**
 * Deals among fresh bazaar and Item Market listings: at least `minPct` under Torn's market value, most profit (reselling
 * the lot at market value) first; and bazaars at $1 (Torn's way of giving away or reserving). `values`: item → name and
 * market value.
 */
export function findDeals(listings: readonly PoolListing[], values: ReadonlyMap<number, { name: string | null; value: number | null }>, opts: { now: number; minPct?: number; limit?: number; freshMs?: number }): { deals: DealRow[]; dollar: DealRow[] } {
  const fresh = opts.freshMs ?? POOL_FRESH_MS;
  const minPct = Math.max(0, Math.min(95, opts.minPct ?? 5));
  const limit = opts.limit ?? POOL_ANSWER_MAX;
  const rows = listings
    .filter((l) => l.source !== 'auction' && opts.now - l.seenAt <= fresh)
    .map((l): DealRow => {
      const v = values.get(l.itemId);
      const value = v?.value && v.value > 0 ? v.value : null;
      return { ...l, itemName: v?.name ?? null, value, below: value ? Math.round(((value - l.price) / value) * 1000) / 10 : null, profit: value ? (value - l.price) * l.qty : null };
    });
  const dollar = rows.filter((r) => r.source === 'bazaar' && r.price <= 1).sort((a, b) => (b.value ?? 0) * b.qty - (a.value ?? 0) * a.qty || b.seenAt - a.seenAt).slice(0, limit);
  const deals = rows.filter((r) => r.price > 1 && r.below !== null && r.below >= minPct).sort((a, b) => (b.profit ?? 0) - (a.profit ?? 0) || b.seenAt - a.seenAt).slice(0, limit);
  return { deals, dollar };
}

// ---------------------------------------------------------------- favorites (the player's own list, lists sync)

export interface MarketFavorite {
  itemId: number;
  /** Buy at or under this (0: just watch). */
  target: number;
  at: number;
}

/** The favorites list from the player's lists document: `{ "<item id>": { target, at } }`. */
export function readFavorites(lists: unknown): MarketFavorite[] {
  const l = obj(obj(lists)?.[MARKET_FAVORITES_LIST]);
  if (!l) return [];
  return Object.entries(l)
    .map(([k, v]): MarketFavorite | null => {
      const id = posInt(k, 1e6);
      const o = obj(v);
      if (!id || !o) return null;
      const target = num(o.target);
      return { itemId: id, target: target !== null && target >= 0 && target <= 1e13 ? Math.round(target) : 0, at: num(o.at) ?? 0 };
    })
    .filter((f): f is MarketFavorite => !!f)
    .slice(0, MARKET_FAVORITES_MAX);
}

/** The favorites as the lists document keeps them. */
export function favoritesDoc(favs: readonly MarketFavorite[]): Record<string, { target: number; at: number }> {
  return Object.fromEntries(favs.slice(0, MARKET_FAVORITES_MAX).map((f) => [String(f.itemId), { target: f.target, at: f.at }]));
}

/** Does this listing meet the favorite's target? */
export const meetsTarget = (f: MarketFavorite, price: number): boolean => f.target > 0 && price <= f.target;

/** Answer for one item's pool listings (`GET /api/market/pool/item/:id`). */
export interface PoolItemAnswer {
  itemId: number;
  listings: PoolListing[];
  at: number;
  termsNeeded: number;
}
